import * as React from 'react';
import { useLocation } from '@reach/router';
import { useStaticQuery, graphql, Script } from 'gatsby';

export const useDefaultMetaData = () => {
	const { site } = useStaticQuery(
		graphql`
			query SiteMetaData {
				site: allDatoCmsDefaultSeoMetaTag {
					nodes {
						description
						title
						twitterHandle
						url
						image {
							url
						}
						siteName
						keywords
					}
				}
			}
		`
	);

	return site.nodes[0];
};

type SeoType = {
	title: string;
	keywords?: string;
	canonicalUrl?: string;
	googleTagManagerScript?: string;
	metaTags?: {
		title: string;
		description: string;
		twitterCard?: string;
		image: {
			url: string;
		};
	};
	children?: string | JSX.Element | JSX.Element[];
};

export const SEO = ({
	metaTags,
	title,
	keywords,
	canonicalUrl,
	googleTagManagerScript,
	children,
}: SeoType) => {
	const { pathname, origin } = useLocation();
	const fallback = useDefaultMetaData();

	const metaDescription = metaTags?.description || fallback.description;
	const metaTitle = metaTags?.title || title || fallback.title;
	const metaTwitterCard = metaTags?.twitterCard || 'summary';
	const metaTwitterHandle = fallback.twitterHandle;
	const metaUrl = `${fallback.url}${pathname}`;
	const metaImage = metaTags?.image?.url || fallback.image?.url;
	const metaSiteName = fallback.siteName;
	const metaKeywords = keywords || fallback.keywords;

	const canonicalTag = [
		{
			rel: 'canonical',
			key: `${origin}${canonicalUrl}`,
			href: `${origin}${canonicalUrl}`,
		},
	];

	/*
    - Add canonical tags
    - Add google tag manager script
  */

	return (
		<>
			<html lang="en" />
			<title>{metaTitle || title}</title>
			<meta name="viewport" content="width=device-width, initial-scale=1.0" />
			{metaKeywords && <meta name="keywords" content={metaKeywords} />}
			<meta name="description" content={metaDescription} />
			{metaImage && <meta name="image" content={metaImage} />}
			<meta name="og:type" content="website" />
			<meta name="og:site_name" content={metaSiteName} />
			<meta property="og:url" content={metaUrl} />
			<meta property="og:title" content={metaTitle} />
			<meta property="og:description" content={metaDescription} />
			{metaImage && <meta property="og:image" content={metaImage} />}
			<meta name="twitter:card" content={metaTwitterCard} />
			{metaTwitterHandle && (
				<meta name="twitter:creator" content={metaTwitterHandle} />
			)}
			{metaTwitterHandle && (
				<meta name="twitter:site" content={metaTwitterHandle} />
			)}
			<meta name="twitter:title" content={metaTitle} />
			<meta name="twitter:description" content={metaDescription} />
			<Script>
				{`
          piAId = '945153'
          piCId = '14552'
          piHostname = 'pi.pardot.com'
          
          ;(function () {
            function async_load() {
              var s = document.createElement('script')
              s.type = 'text/javascript'
              s.src =
                ('https:' == document.location.protocol ? 'https://pi' : 'http://cdn') +
                '.pardot.com/pd.js'
              var c = document.getElementsByTagName('script')[0]
              c.parentNode.insertBefore(s, c)
            }
            if (window.attachEvent) {
              window.attachEvent('onload', async_load)
            } else {
              window.addEventListener('load', async_load, false)
            }
          })() 
        `}
			</Script>
			{children}
		</>
	);
};
